import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface Rental {
  id: string;
  renterName: string;
  listingTitle: string;
  startDate: string;
  endDate: string;
  months: number;
  amount: number;
  host_payout: number;
  status: string;
}

export default function RentalHistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'active' | 'cancelled'>('all');
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRentals = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);

        // Get user's listing IDs
        const { data: userListings } = await supabase
          .from('listings')
          .select('id')
          .eq('host_id', user.id);

        const listingIds = userListings?.map(l => l.id) || [];

        if (listingIds.length === 0) {
          setRentals([]);
          setLoading(false);
          return;
        }

        // Fetch bookings where user is the host
        const { data: bookings, error } = await supabase
          .from('bookings')
          .select(`
            id,
            start_date,
            end_date,
            months,
            total_amount,
            host_payout,
            status,
            renter_id,
            listing_id,
            listings:listing_id (
              id,
              title
            )
          `)
          .in('listing_id', listingIds)
          .in('status', ['active', 'confirmed', 'completed'])
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Fetch renter profiles
        const renterIds = [...new Set((bookings || []).map(b => b.renter_id))];
        let renterProfiles: Record<string, string> = {};

        if (renterIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', renterIds);

          if (profiles) {
            renterProfiles = profiles.reduce((acc, p) => {
              acc[p.id] = p.full_name || 'Guest';
              return acc;
            }, {} as Record<string, string>);
          }
        }

        // Convert to Rental format
        const realRentals: Rental[] = (bookings || []).map(booking => ({
          id: booking.id,
          renterName: renterProfiles[booking.renter_id] || 'Guest',
          listingTitle: booking.listings?.title || 'Unknown Listing',
          startDate: booking.start_date,
          endDate: booking.end_date,
          months: booking.months || 1,
          amount: booking.total_amount || 0,
          host_payout: booking.host_payout || booking.total_amount || 0,
          status: booking.status === 'confirmed' ? 'active' : booking.status,
        }));

        setRentals(realRentals);
      } catch (_error: unknown) {
        // silently handle error
      } finally {
        setLoading(false);
      }
    };

    fetchRentals();
  }, [user?.id]);

  const filteredRentals = rentals.filter(rental => {
    const matchesSearch = rental.renterName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rental.listingTitle.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || rental.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status: string) => {
    const styles = {
      completed: 'bg-green-100 text-green-800',
      active: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return styles[status as keyof typeof styles];
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      completed: 'ri-checkbox-circle-line',
      active: 'ri-time-line',
      cancelled: 'ri-close-circle-line',
    };
    return icons[status as keyof typeof icons];
  };

  const totalEarnings = rentals
    .reduce((sum, r) => sum + r.host_payout, 0);

  const activeRentals = rentals.filter(r => r.status === 'active').length;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-[#14B8A6] mb-4 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Rental History</h1>
            <p className="text-gray-600 mt-2">Track who rented from you, duration, and dates</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Rentals</p>
                  <p className="text-3xl font-bold text-gray-900">{rentals.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-history-line text-blue-600 text-2xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Active Rentals</p>
                  <p className="text-3xl font-bold text-blue-600">{activeRentals}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-blue-600 text-2xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Earned</p>
                  <p className="text-3xl font-bold text-green-600">${totalEarnings.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-green-600 text-2xl"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-search-line text-gray-400"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Search by renter name or listing..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="md:w-48">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm pr-8"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Rentals List */}
          {filteredRentals.length > 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Renter
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Listing
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        End Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredRentals.map((rental) => (
                      <tr key={rental.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-semibold text-gray-900">{rental.renterName}</p>
                            <p className="text-sm text-gray-500">{rental.renterEmail}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{rental.listingTitle}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">
                            {new Date(rental.startDate).toLocaleDateString('en-AU', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">
                            {new Date(rental.endDate).toLocaleDateString('en-AU', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">{rental.months} {rental.months === 1 ? 'month' : 'months'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-bold text-gray-900">${rental.host_payout.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(rental.status)}`}>
                            <i className={getStatusIcon(rental.status)}></i>
                            {rental.status.charAt(0).toUpperCase() + rental.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-history-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No rental history found</h3>
              <p className="text-gray-600">
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your search or filters'
                  : "You don't have any rental history yet"}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
